import Table from "../ReusableComponents/Table";
import {
    driverDeclarationsTableContents,
    driverDeclarationsMenuGroups,
    declarationsOrderByOptions,
} from "./DriverDeclarationsTableInit";
import { driverDeclarationsMenuItems } from "./DriverDeclarationsTableMenuOptions";
import useDriverDeclarationsTable from "./useDriverDeclarationsTable";
import PaginationNav from "../PaginationNav";
import DriverDeclarationsTableFilters from "./DriverDeclarationsTableFilters";
import {
    ApiEDeclarationsResponse,
    DriverDeclaration,
} from "../../models/api/admin/eDeclarations";
import ApiError from "../../api/common/apiError";
import { useEffect, useState } from "react";
import { TableCheckbox } from "../ReusableComponents/Table/tableTypes";
import { DeclarationTableFilters } from "../../api/admin/eDeclarations";
import {
    tableHeaderOptionsEnum,
    useTableHeaders,
} from "../../hooks/useTableHeaders";
import useTableStore from "../../stores/useTableStore";
import { handleSortColumnChange } from "../../utils/tableUtils";

type DriverDeclarationsTableProps = {
    eDeclarationsList?: ApiEDeclarationsResponse;
    isLoadingEDeclarationsList: boolean;
    eDeclarationsListError?: ApiError;
    pageNumber: number;
    setPageNumber: (value: number) => void;
    pageSize: number;
    setPageSize: (value: number) => void;
    refetchEDeclarationsList: () => void;
    refetchCompanyDeclarationAnalysis: () => void;
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    declarationTableFiltersStore: DeclarationTableFilters;
    handleResetFilters: () => void;
    tableRef?: React.MutableRefObject<HTMLDivElement | null>;
    sortOrder: "asc" | "desc";
    setSortOrder: (val: string) => void;
    orderBy: string;
    setOrderBy: (val: string) => void;
};
export default function DriverDeclarationsTable({
    eDeclarationsList,
    isLoadingEDeclarationsList,
    eDeclarationsListError,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    refetchEDeclarationsList,
    refetchCompanyDeclarationAnalysis,
    searchQuery,
    setSearchQuery,
    declarationTableFiltersStore,
    handleResetFilters,
    tableRef,
    sortOrder,
    setSortOrder,
    orderBy,
    setOrderBy,
}: DriverDeclarationsTableProps) {
    const handleRefetch = () => {
        refetchCompanyDeclarationAnalysis();
        refetchEDeclarationsList();
    };
    const {
        switchDeclarationTypeAction,
        isUpdatingDeclarationType,
        editDriverClickHandler,
        declarationRequestAction,
    } = useDriverDeclarationsTable(handleRefetch);

    const { setTableHeaders, setHiddenCols } = useTableHeaders(
        tableHeaderOptionsEnum.declarations,
        true
    );
    const tableHeadersDeclaration = useTableStore(
        (state) => state.tableHeadersDeclarations
    );
    const hiddenColsDeclarations = useTableStore(
        (state) => state.hiddenColsDeclarations
    );

    const [selectedCheckboxes, setSelectedCheckboxes] = useState<
        TableCheckbox<DriverDeclaration>[]
    >([]);

    // reset selected checkboxes when table page changes
    useEffect(() => {
        setSelectedCheckboxes([]);
    }, [pageNumber]);

    return (
        <div>
            <div className="flex flex-col gap-6">
                <DriverDeclarationsTableFilters
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedCheckboxes={selectedCheckboxes}
                    setSelectedCheckboxes={setSelectedCheckboxes}
                    refetchEDeclarationsList={handleRefetch}
                    declarationTableFiltersStore={declarationTableFiltersStore}
                    handleResetFilters={handleResetFilters}
                    isLoadingEDeclarationsList={isLoadingEDeclarationsList}
                />
                <>
                    <Table
                        tableHeaders={tableHeadersDeclaration}
                        setTableHeaders={(value) =>
                            setTableHeaders(
                                value,
                                tableHeaderOptionsEnum.declarations
                            )
                        }
                        tableContents={driverDeclarationsTableContents(
                            eDeclarationsList?.items
                        )}
                        menuItems={(data) =>
                            driverDeclarationsMenuItems(
                                data,
                                editDriverClickHandler,
                                declarationRequestAction,
                                switchDeclarationTypeAction,
                                isUpdatingDeclarationType
                            )
                        }
                        menuGroups={driverDeclarationsMenuGroups}
                        hiddenCols={hiddenColsDeclarations}
                        setHiddenCols={(value) =>
                            setHiddenCols(
                                value,
                                tableHeaderOptionsEnum.declarations
                            )
                        }
                        orderByOptions={declarationsOrderByOptions}
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        handleSortColumn={(newOrderBy?: string) => {
                            if (!newOrderBy) return;
                            handleSortColumnChange(
                                orderBy,
                                newOrderBy,
                                sortOrder,
                                setSortOrder,
                                setOrderBy
                            );
                        }}
                        isLoading={isLoadingEDeclarationsList}
                        tableRef={tableRef}
                        checkedBoxes={selectedCheckboxes}
                        setCheckedBoxes={setSelectedCheckboxes}
                    />
                    {eDeclarationsListError && errorMessage}
                    {eDeclarationsList && (
                        <>
                            {eDeclarationsList.items.length === 0 && (
                                <div className=" mt-3 rounded border-2 border-S2D-error-40 bg-S2D-error-90 p-4 text-S2D-neutral-10">
                                    No declarations found
                                </div>
                            )}
                            <PaginationNav
                                setPageNumber={setPageNumber}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                totalCount={eDeclarationsList.totalCount}
                                totalPages={eDeclarationsList.totalPages}
                                currentPage={eDeclarationsList.pageNumber}
                                hasPreviousPage={
                                    eDeclarationsList.hasPreviousPage
                                }
                                hasNextPage={eDeclarationsList.hasNextPage}
                            />
                        </>
                    )}
                </>
            </div>
        </div>
    );
}

const errorMessage = (
    <div className=" mt-3 rounded border-2 border-S2D-error-40 bg-S2D-error-90 p-4 text-S2D-neutral-10">
        Error loading declarations
    </div>
);

